import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import type { CreateSafetyFindingRequest, SafetyFindingRow, SuspensionRow } from "../../api/staffTypes";
import { Banner, Button, Card, Checkbox, Dialog, Input, PageHeader, QueryView, RadioGroup, Table, Textarea } from "../../components/ui";
import { AwaitingSecondBanner, isUuid, Mono, reasonError, StaffRejection, StepUpNote, useStaffCommand, when } from "./staffCommon";

// Safety findings and suspensions (ARCHITECTURE.md §6/§7; migrations
// 0026, 0032, 0048, 0068; safety.go). Raising a finding synchronously
// blocks the scope's capture, recognition, reservation and payout paths;
// migration 0068 (G-51/G-53/G-54/G-55) added the reads below and made
// the financial fan-out wait for a SECOND, distinct reviewer's confirm
// before the safety_fan_out job (cmd/background-worker) ever reverses
// anything captured. Every write here now needs a recent MFA step-up
// (svc.RequireRecentMFA on every safety.go route) and is idempotent.

export function SafetyPage() {
  const [params] = useSearchParams();
  const prefill = params.get("suspension") ?? "";
  return (
    <div className="stack-lg">
      <PageHeader title="Safety findings" description="Raise a finding, confirm its fan-out, or lift a suspension. A finding's financial fan-out needs a second, distinct reviewer to confirm it." />
      <FindingsCard />
      <SuspensionsCard />
      <CreateFindingCard />
      <ConfirmCard prefill={prefill} />
      <ResolveCard prefill={prefill} />
      <LiftCard prefill={prefill} />
    </div>
  );
}

const FINDING_STATUS: Record<string, string> = {
  in_progress: "Fan-out in progress",
  resolved: "Fan-out resolved",
};

function FindingsCard() {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: ["staff-safety-findings"], queryFn: () => ep.staff.safety.list() });
  return (
    <Card title="Safety findings" subtitle="Every safety-kind suspension, its confirmation and its fan-out progress (G-51).">
      <QueryView query={q} loadingLabel="Loading safety findings">
        {(data) => (
          <Table
            caption="Safety findings"
            rows={data.findings}
            rowKey={(f) => f.suspension_id}
            empty={<p className="muted small">No safety findings.</p>}
            columns={[
              { key: "scope", header: "Scope", cell: (f: SafetyFindingRow) => `${f.scope_type} · ${f.scope_id.slice(0, 8)}` },
              { key: "reason", header: "Reason", cell: (f) => f.reason },
              {
                key: "confirmed",
                header: "Confirmation",
                cell: (f) => `${f.confirmation.approvals} of ${f.confirmation.approvals_required}${f.confirmation.confirmed ? " — confirmed" : ""}`,
              },
              { key: "fanout", header: "Fan-out", cell: (f) => (f.fan_out ? `${FINDING_STATUS[f.fan_out.status] ?? f.fan_out.status} (${f.fan_out.processed} processed)` : "Not confirmed yet") },
              { key: "lifted", header: "Lifted", cell: (f) => (f.lifted_at ? when(f.lifted_at) : "No") },
              {
                key: "act",
                header: "Actions",
                cell: (f) => (
                  <Link to={`/staff/safety?suspension=${f.suspension_id}`}>
                    <code>{f.suspension_id.slice(0, 8)}</code>
                  </Link>
                ),
              },
            ]}
          />
        )}
      </QueryView>
    </Card>
  );
}

function SuspensionsCard() {
  const ep = useEndpoints();
  const [active, setActive] = useState<"true" | "false" | "">("true");
  const q = useQuery({ queryKey: ["staff-suspensions", active], queryFn: () => ep.staff.safety.suspensions(active || undefined) });
  return (
    <Card
      title="Suspensions"
      subtitle="Any scope, any kind (G-51)."
      actions={
        <RadioGroup
          legend="Filter"
          name="suspension-filter"
          value={active}
          onChange={setActive}
          options={[
            { value: "true", label: "Active" },
            { value: "false", label: "Lifted" },
            { value: "", label: "All" },
          ]}
        />
      }
    >
      <QueryView query={q} loadingLabel="Loading suspensions">
        {(data) => (
          <Table
            caption="Suspensions"
            rows={data.suspensions}
            rowKey={(s) => s.suspension_id}
            empty={<p className="muted small">No suspensions match this filter.</p>}
            columns={[
              { key: "kind", header: "Kind", cell: (s: SuspensionRow) => s.kind },
              { key: "scope", header: "Scope", cell: (s) => `${s.scope_type} · ${s.scope_id.slice(0, 8)}` },
              { key: "reason", header: "Reason", cell: (s) => s.reason },
              { key: "since", header: "Since", cell: (s) => when(s.effective_at) },
              { key: "lifted", header: "Lifted", cell: (s) => (s.lifted_at ? when(s.lifted_at) : "No") },
            ]}
          />
        )}
      </QueryView>
    </Card>
  );
}

function CreateFindingCard() {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const [scopeType, setScopeType] = useState<"site" | "campaign" | "">("");
  const [scopeId, setScopeId] = useState("");
  const [reason, setReason] = useState("");
  const [effectiveAt, setEffectiveAt] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<CreateSafetyFindingRequest | null>(null);
  const cmd = useStaffCommand((req: CreateSafetyFindingRequest, opts) => ep.staff.safety.createFinding(req, opts), () => {
    setConfirming(null);
    void qc.invalidateQueries({ queryKey: ["staff-safety-findings"] });
    void qc.invalidateQueries({ queryKey: ["staff-suspensions"] });
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!scopeType) return setFormError("Choose a site or a campaign.");
    if (!isUuid(scopeId)) return setFormError("Enter the scope's id (a UUID).");
    const re = reasonError(reason);
    if (re) return setFormError(re);
    let eff: string | undefined;
    if (effectiveAt.trim()) {
      const d = new Date(effectiveAt.trim());
      if (Number.isNaN(d.getTime()) || !/[zZ]|[+-]\d\d:\d\d$/.test(effectiveAt.trim())) {
        return setFormError("Give the boundary as an RFC 3339 time with a zone, like 2026-09-25T10:00:00Z.");
      }
      eff = effectiveAt.trim();
    }
    setFormError(null);
    cmd.reset();
    setConfirming({ scope_type: scopeType, scope_id: scopeId.trim().toLowerCase(), reason: reason.trim(), ...(eff ? { effective_at: eff } : {}) });
  }

  const res = cmd.data;
  return (
    <Card title="Raise a safety finding">
      {res ? (
        <Banner tone="success" live="polite" title="Finding raised; the scope is suspended">
          Suspension <Mono>{res.suspension_id}</Mono>, effective from {when(res.effective_at)}. The fan-out only starts once a
          SECOND, distinct reviewer confirms it below.{" "}
          <Link to={`/staff/safety?suspension=${res.suspension_id}`}>Confirm, resolve or lift it later</Link>
        </Banner>
      ) : null}
      <form className="stack" onSubmit={submit} noValidate>
        <RadioGroup
          legend="Scope"
          name="scope-type"
          value={scopeType}
          onChange={setScopeType}
          options={[
            { value: "site", label: "Site" },
            { value: "campaign", label: "Campaign" },
          ]}
        />
        <Input label="Scope id" mono value={scopeId} onChange={(e) => setScopeId(e.target.value)} hint="A client version is suspended from the client matrix instead." />
        <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
        <Input
          label="Effective-time boundary"
          optional
          mono
          value={effectiveAt}
          onChange={(e) => setEffectiveAt(e.target.value)}
          hint="Captured deliveries from this time on are reversed. Empty means no known onset: every captured delivery in scope."
        />
        <StepUpNote required />
        <p className="xs muted">One reviewer creates the immediate hold; a second, distinct reviewer's confirmation below starts the financial fan-out.</p>
        {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
        {cmd.error && !confirming ? <StaffRejection error={cmd.error} /> : null}
        <div>
          <Button type="submit" variant="danger">
            Raise finding…
          </Button>
        </div>
      </form>
      <Dialog
        open={!!confirming}
        alert
        onClose={() => setConfirming(null)}
        dismissible={!cmd.isPending}
        title="Suspend this scope?"
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirming(null)} disabled={cmd.isPending}>
              Cancel
            </Button>
            <Button variant="danger" loading={cmd.isPending} onClick={() => confirming && cmd.run(confirming)}>
              Raise finding
            </Button>
          </>
        }
      >
        {confirming ? (
          <div className="stack-sm small">
            <span>
              The {confirming.scope_type} <Mono>{confirming.scope_id}</Mono> stops being served, captured, recognized and paid out at once.
              A second, distinct reviewer then has to confirm before the fan-out reverses anything{" "}
              {confirming.effective_at ? <>captured from {when(confirming.effective_at)}</> : "captured in scope, with no time limit"}.
            </span>
            {cmd.error ? <StaffRejection error={cmd.error} /> : null}
          </div>
        ) : null}
      </Dialog>
    </Card>
  );
}

function ConfirmCard({ prefill }: { prefill: string }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const [id, setId] = useState(prefill);
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [acceptUpdated, setAcceptUpdated] = useState(false);
  const cmd = useStaffCommand(
    (req: { id: string; notes: string; accept: boolean }, opts) => ep.staff.safety.confirm(req.id, req.notes, req.accept, opts),
    () => {
      void qc.invalidateQueries({ queryKey: ["staff-safety-findings"] });
    },
  );
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!isUuid(id)) return setFormError("Enter the finding's suspension id.");
    const re = reasonError(notes);
    if (re) return setFormError("Give confirmation notes.");
    setFormError(null);
    cmd.run({ id: id.trim().toLowerCase(), notes: notes.trim(), accept: acceptUpdated });
  }

  const res = cmd.data;
  const impactChanged =
    cmd.error instanceof ApiError && cmd.error.code === "impact_changed"
      ? { previous: cmd.error.detail?.previous_impact as { reservations: number; campaigns: number } | undefined, current: cmd.error.detail?.current_impact as { reservations: number; campaigns: number } | undefined }
      : null;

  return (
    <Card title="Confirm a finding's fan-out" subtitle="The SECOND, distinct reviewer: without this, nothing captured is ever reversed.">
      {res && res.result !== "awaiting_second_approval" ? (
        <Banner tone={res.result === "confirmed" ? "success" : "info"} live="polite" title={res.result.replace(/_/g, " ")}>
          Suspension <Mono>{res.suspension_id}</Mono>.
          {res.impact_preview ? (
            <>
              {" "}
              Affects {res.impact_preview.reservations} reservation(s) across {res.impact_preview.campaigns} campaign(s).
            </>
          ) : null}
        </Banner>
      ) : null}
      {res?.result === "awaiting_second_approval" ? <AwaitingSecondBanner approvals={res.approvals} required={res.approvals_required} /> : null}
      <form className="stack" onSubmit={submit} noValidate>
        <Input label="Suspension id" mono value={id} onChange={(e) => setId(e.target.value)} />
        <Textarea label="Confirmation notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        {impactChanged ? (
          <Banner tone="warning" title="The impact changed since it was computed">
            <div className="stack-sm">
              {impactChanged.previous ? <span>Previously: {impactChanged.previous.reservations} reservation(s), {impactChanged.previous.campaigns} campaign(s).</span> : null}
              {impactChanged.current ? <span>Now: {impactChanged.current.reservations} reservation(s), {impactChanged.current.campaigns} campaign(s).</span> : null}
              <Checkbox label="I've reviewed the current impact and want to confirm anyway" checked={acceptUpdated} onChange={(e) => setAcceptUpdated(e.target.checked)} />
            </div>
          </Banner>
        ) : null}
        <StepUpNote required />
        {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
        {cmd.error && !impactChanged ? <StaffRejection error={cmd.error} /> : null}
        <div>
          <Button type="submit" variant="danger" loading={cmd.isPending}>
            Confirm
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ResolveCard({ prefill }: { prefill: string }) {
  const ep = useEndpoints();
  const [id, setId] = useState(prefill);
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const cmd = useStaffCommand((req: { id: string; notes: string }, opts) => ep.staff.safety.resolve(req.id, req.notes, opts));
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!isUuid(id)) return setFormError("Enter the finding's suspension id.");
    const re = reasonError(notes);
    if (re) return setFormError("Say why the fan-out is complete. It's recorded as the resolution notes.");
    setFormError(null);
    cmd.run({ id: id.trim().toLowerCase(), notes: notes.trim() });
  }
  return (
    <Card title="Confirm a finding's fan-out is resolved" subtitle="A human judgment: the server doesn't check the fan-out is complete. Needs a confirmed finding first.">
      {cmd.data ? (
        <Banner tone="success" live="polite" title={cmd.data.result === "already_resolved" ? "Already resolved" : "Resolved"}>
          Suspension <Mono>{cmd.data.suspension_id}</Mono>.
        </Banner>
      ) : null}
      <form className="stack" onSubmit={submit} noValidate>
        <Input label="Suspension id" mono value={id} onChange={(e) => setId(e.target.value)} />
        <Textarea label="Resolution notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        <StepUpNote required />
        {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
        {cmd.error ? <StaffRejection error={cmd.error} /> : null}
        <div>
          <Button type="submit" variant="secondary" loading={cmd.isPending}>
            Mark resolved
          </Button>
        </div>
      </form>
    </Card>
  );
}

function LiftCard({ prefill }: { prefill: string }) {
  const ep = useEndpoints();
  const [id, setId] = useState(prefill);
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const cmd = useStaffCommand((req: { id: string; reason: string }, opts) => ep.staff.safety.lift(req.id, req.reason, opts), () => setConfirming(false));
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!isUuid(id)) return setFormError("Enter the suspension id.");
    const re = reasonError(reason);
    if (re) return setFormError(re);
    setFormError(null);
    cmd.reset();
    setConfirming(true);
  }
  const res = cmd.data;
  return (
    <Card title="Lift a suspension" subtitle="Readmits the scope. Dual-controlled only once a CONFIRMED safety finding; every other suspension lifts on one reviewer.">
      {res && res.result !== "awaiting_second_approval" ? (
        <Banner tone={res.applied ? "success" : "info"} live="polite" title={res.result === "already_lifted" ? "Already lifted" : "Lifted"}>
          {res.kind ? `${res.kind} suspension of ${res.scope_type} ` : null}
          {res.scope_id ? <Mono>{res.scope_id}</Mono> : null}
          {res.scope_version !== undefined ? `; scope version now ${res.scope_version}` : ""}.
        </Banner>
      ) : null}
      {res?.result === "awaiting_second_approval" ? (
        <>
          <AwaitingSecondBanner approvals={res.approvals} required={res.approvals_required} />
          {res.impact_of_lift ? (
            <p className="small">
              Would readmit {res.impact_of_lift.reservations} reservation(s) across {res.impact_of_lift.campaigns} campaign(s).
            </p>
          ) : null}
        </>
      ) : null}
      <form className="stack" onSubmit={submit} noValidate>
        <Input label="Suspension id" mono value={id} onChange={(e) => setId(e.target.value)} />
        <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        <StepUpNote required />
        {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
        {cmd.error && !confirming ? <StaffRejection error={cmd.error} /> : null}
        {cmd.error && !confirming && (cmd.error as { code?: string }).code === "client_version_suspension" ? (
          <p className="small">
            <Link to="/staff/client-matrix">Go to the client matrix</Link>
          </p>
        ) : null}
        <div>
          <Button type="submit" variant="secondary">
            Lift…
          </Button>
        </div>
      </form>
      <Dialog
        open={confirming}
        alert
        onClose={() => setConfirming(false)}
        dismissible={!cmd.isPending}
        title="Lift this suspension?"
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)} disabled={cmd.isPending}>
              Cancel
            </Button>
            <Button variant="danger" loading={cmd.isPending} onClick={() => cmd.run({ id: id.trim().toLowerCase(), reason: reason.trim() })}>
              Lift suspension
            </Button>
          </>
        }
      >
        <div className="stack-sm small">
          <span>
            Serving resumes for this scope and handles issued during the suspension stop working. For a CONFIRMED safety finding this
            needs a second, distinct reviewer too.
          </span>
          {cmd.error ? <StaffRejection error={cmd.error} /> : null}
        </div>
      </Dialog>
    </Card>
  );
}
